import { CATEGORIES, GROUPS } from '@/data/groups';
import { createRandom } from '@/data/seed';
import { addDays, format, startOfWeek } from 'date-fns';

// 매트릭스·히트맵의 순수 로직 — 열(주) 계산, 색 단계(버킷), 그룹 평균, 입력 검증, 더미 값 시드.

export const WEEK_COUNT = 12;
export const MIN_VALUE = 0;
export const MAX_VALUE = 100;
/** 색 단계 수 — 범례·셀 색·테스트가 모두 이 값을 기준으로 한다. */
export const BUCKET_COUNT = 5;

export interface MatrixRow {
  id: string;
  name: string;
}
export interface MatrixGroup {
  id: string;
  name: string;
  rows: MatrixRow[];
}

/** 그룹 → 하위 행 2단 구조. src/data의 그룹·카테고리를 읽기 전용으로 쓴다. */
export const MATRIX_GROUPS: MatrixGroup[] = GROUPS.map((group) => ({
  id: group.id,
  name: group.name,
  rows: CATEGORIES.filter((category) => category.groupId === group.id).map((category) => ({
    id: category.id,
    name: category.name,
  })),
}));

/** 최근 n주의 시작일(월요일, yyyy-MM-dd) — 오래된 주부터, 마지막이 이번 주. */
export function lastWeeks(today: Date, count = WEEK_COUNT): string[] {
  const thisWeek = startOfWeek(today, { weekStartsOn: 1 });
  return Array.from({ length: count }, (_, i) =>
    format(addDays(thisWeek, (i - (count - 1)) * 7), 'yyyy-MM-dd'),
  );
}

export const cellKey = (rowId: string, week: string) => `${rowId}|${week}`;

/** 한 단계의 폭 — 0~100을 5등분하면 20씩 */
const BUCKET_SIZE = (MAX_VALUE - MIN_VALUE) / BUCKET_COUNT;

/**
 * 값 → 색 단계(0~4). 0–19, 20–39, 40–59, 60–79, 80–100.
 * 100은 6번째 단계가 아니라 마지막(4) 단계에 넣는다.
 */
export function bucketOf(value: number): number {
  return Math.min(BUCKET_COUNT - 1, Math.max(0, Math.floor((value - MIN_VALUE) / BUCKET_SIZE)));
}

/** 범례용 구간 라벨 — bucketOf와 같은 경계를 쓴다. */
export function bucketRanges(): { bucket: number; label: string }[] {
  return Array.from({ length: BUCKET_COUNT }, (_, bucket) => {
    const from = MIN_VALUE + bucket * BUCKET_SIZE;
    const to = bucket === BUCKET_COUNT - 1 ? MAX_VALUE : from + BUCKET_SIZE - 1;
    return { bucket, label: `${String(from)}–${String(to)}` };
  });
}

/** 그룹 행에 보여 줄 평균(반올림). 하위 행이 없으면 null. */
export function average(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

export type ParsedValue = { value: number } | { error: string };

/** 셀 값 입력 검증 — 0~100 정수. 무엇이 틀렸는지 말한다. */
export function parseValue(raw: string): ParsedValue {
  const text = raw.trim();
  if (text === '') {
    return { error: '값을 입력하세요.' };
  }
  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    return { error: '값을 숫자로 입력하세요.' };
  }
  const value = Number(text);
  if (!Number.isInteger(value)) {
    return { error: '값은 정수로 입력하세요.' };
  }
  if (value < MIN_VALUE || value > MAX_VALUE) {
    return { error: `값은 ${String(MIN_VALUE)}~${String(MAX_VALUE)} 사이여야 합니다.` };
  }
  return { value };
}

/**
 * 더미 값 — 행마다 기준 수준이 달라 색이 고르게 퍼지고, 주가 지날수록 완만히 오르내려
 * 가로로 읽을 때 추세가 보인다. 시드 고정이라 열 때마다 같은 모양이다.
 */
export function seedValues(groups: MatrixGroup[], weeks: string[]): Map<string, number> {
  const random = createRandom(1801);
  const values = new Map<string, number>();
  for (const row of groups.flatMap((group) => group.rows)) {
    const base = random.int(15, 85);
    const drift = random.int(-4, 4);
    weeks.forEach((week, index) => {
      const noisy = base + drift * index + random.int(-15, 15);
      values.set(cellKey(row.id, week), Math.min(MAX_VALUE, Math.max(MIN_VALUE, noisy)));
    });
  }
  return values;
}
